import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Typography,
  message,
  Row,
  Col,
  Card,
  Badge
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined,
  TagOutlined
} from '@ant-design/icons';
import { categoriasService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

function Categorias() {
  const { user } = useAuth();
  const canEdit = user?.rol === 'root' || user?.rol === 'admin';
  const canDelete = user?.rol === 'root';
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  const fetchCategorias = useCallback(async () => {
    setLoading(true);
    try {
      const res = await categoriasService.getAll();
      setCategorias(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategorias();
  }, [fetchCategorias]);

  const filteredCategorias = categorias.filter((cat) =>
    cat.nombre.toLowerCase().includes(search.toLowerCase())
  );

  const openCreateModal = () => {
    setEditingCategoria(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingCategoria(record);
    form.setFieldsValue({
      nombre: record.nombre,
      descripcion: record.descripcion || ''
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingCategoria) {
        await categoriasService.update(editingCategoria.id, values);
        message.success('Categoría actualizada exitosamente');
      } else {
        await categoriasService.create(values);
        message.success('Categoría creada exitosamente');
      }

      setModalOpen(false);
      fetchCategorias();
    } catch (error) {
      if (error.errorFields) return; // validation error, do nothing
      message.error(error.displayMessage || 'Error al guardar categoría');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await categoriasService.delete(record.id);
      message.success(`Categoría "${record.nombre}" eliminada exitosamente`);
      fetchCategorias();
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar la categoría');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
      sorter: (a, b) => a.id - b.id,
      render: (id) => <Text type="secondary" style={{ fontSize: 12 }}>#{id}</Text>
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      render: (nombre) => (
        <Space>
          <TagOutlined style={{ color: '#1677ff' }} />
          <Text strong>{nombre}</Text>
        </Space>
      )
    },
    {
      title: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      ellipsis: true,
      render: (desc) =>
        desc ? (
          <Tooltip title={desc}>
            <Text type="secondary">{desc}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary" style={{ fontStyle: 'italic' }}>Sin descripción</Text>
        )
    },
    {
      title: 'Total Productos',
      dataIndex: 'total_productos',
      key: 'total_productos',
      width: 140,
      align: 'center',
      sorter: (a, b) => (parseInt(a.total_productos) || 0) - (parseInt(b.total_productos) || 0),
      render: (val) => {
        const count = parseInt(val) || 0;
        return count > 0 ? (
          <Badge
            count={count}
            style={{ backgroundColor: '#1677ff' }}
            overflowCount={999}
          />
        ) : (
          <Tag color="default">0</Tag>
        );
      }
    },
    {
      title: 'Fecha Creación',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a, b) => dayjs(a.created_at).unix() - dayjs(b.created_at).unix(),
      render: (val) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '—'}
        </Text>
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 110,
      fixed: 'right',
      render: (_, record) => {
        const totalProductos = parseInt(record.total_productos) || 0;
        const canDeleteRow = canDelete && totalProductos === 0;
        return (
          <Space size={4}>
            {canEdit && (
              <Tooltip title="Editar categoría">
                <Button
                  type="primary"
                  ghost
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => openEditModal(record)}
                />
              </Tooltip>
            )}

            {canDelete && (
              <Tooltip
                title={
                  canDeleteRow
                    ? 'Eliminar categoría'
                    : totalProductos > 0
                    ? 'No se puede eliminar, tiene productos asociados'
                    : 'Sin permisos para eliminar'
                }
              >
                <span>
                  <Popconfirm
                    title="¿Eliminar categoría?"
                    description={`La categoría "${record.nombre}" será eliminada permanentemente.`}
                    onConfirm={() => handleDelete(record)}
                    okText="Sí, eliminar"
                    cancelText="Cancelar"
                    icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                    okButtonProps={{ danger: true }}
                    disabled={!canDeleteRow}
                  >
                    <Button
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      disabled={!canDeleteRow}
                    />
                  </Popconfirm>
                </span>
              </Tooltip>
            )}
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0 }}>Categorías</Title>
          <Text type="secondary">Gestión de categorías de productos</Text>
        </div>
      </div>

      {/* Filters & Actions */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={10} lg={8}>
            <Input
              placeholder="Buscar por nombre..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                setSearch('');
                fetchCategorias();
              }}
            >
              Actualizar
            </Button>
          </Col>
          {canEdit && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Nueva Categoría
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={filteredCategorias}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 700 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} categoría${total !== 1 ? 's' : ''}`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron categorías' }}
        />
      </Card>

      {/* Modal Create/Edit */}
      <Modal
        title={
          editingCategoria
            ? `Editar Categoría: ${editingCategoria.nombre}`
            : 'Nueva Categoría'
        }
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editingCategoria ? 'Guardar Cambios' : 'Crear Categoría'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={520}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          style={{ marginTop: 16 }}
        >
          <Form.Item
            name="nombre"
            label="Nombre de la Categoría"
            rules={[
              { required: true, message: 'El nombre es requerido' },
              { max: 100, message: 'Máximo 100 caracteres' },
              { whitespace: true, message: 'El nombre no puede estar vacío' }
            ]}
          >
            <Input
              placeholder="Ej: Electrónicos, Herramientas, Materiales..."
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="descripcion"
            label="Descripción"
            rules={[{ max: 500, message: 'Máximo 500 caracteres' }]}
          >
            <TextArea
              placeholder="Descripción de la categoría (opcional)"
              rows={3}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Categorias;
